import { Option } from "effect";
import type { HttpClient } from "effect/unstable/http";
import type * as AsyncData from "foldkit/asyncData";
import type * as Update from "foldkit/update";

import type { ApiError } from "../api/errors";
import type { FeaturesConfig } from "../api/schemas/account";
import type { AttributeSchemaList } from "../api/schemas/attributeSchema";
import type { LedgerBalance } from "../api/schemas/ledger";
import type { TagList } from "../api/schemas/tags";
import type { Route } from "../app/routes";
import type { Theme } from "../app/theme";
import type { User } from "../auth/model";
import type { ClerkTokenService } from "../auth/tokenService";

/** Services every page Command may need (KTD4). */
export type Api = HttpClient.HttpClient | ClerkTokenService;

/**
 * Root state a page reads but never owns (KTD3). The root recomputes it on
 * every fold and every render; pages must not copy it into their Model.
 */
export type Shared = Readonly<{
	generation: number;
	/** The current route, so page links keep `asOf` through `linkHref` (KTD3). */
	route: Route;
	asOf: Option.Option<string>;
	user: User;
	balance: AsyncData.AsyncData<LedgerBalance, ApiError>;
	features: AsyncData.AsyncData<FeaturesConfig, ApiError>;
	/** Account-wide tag usage, fetched once at the root (KTD3); the Entry combobox reads it. */
	tags: AsyncData.AsyncData<TagList, ApiError>;
	/** Every attribute schema entry (both entity types), fetched once at the root (KTD3). */
	attributeSchema: AsyncData.AsyncData<AttributeSchemaList, ApiError>;
	/** The developer-toggled integrations flag (KTD3); the Developer page shows it and reports toggles upward. */
	integrationsEnabled: boolean;
	/** The PostHog `billing-preview` flag: Billing exists only while it is on. */
	billingPreviewEnabled: boolean;
	/** PostHog `a2a-context` AND `features.a2aContextEnabled`: Tasks, Knowledge, Agents and the record tabs. */
	a2aContextEnabled: boolean;
	theme: Theme;
}>;

/** `OutMessage` defaults to `never`, so a page without facts for the root returns a plain `Update.Return`. */
export type PageReturn<
	Model,
	Message,
	OutMessage = never,
> = Update.ReturnWithOutMessage<Model, Message, OutMessage, Api>;

/**
 * The page Submodel contract (KTD2). One module per page under
 * `src/pages/<page>/`, registered in `src/pages/index.ts`.
 *
 * - `init` runs on an `entered` transition. Issue the page's fetch Commands here, tagged with
 *   `shared.generation` and `shared.asOf`.
 * - `changedRoute` runs on a `stayed` transition (same route tag, different
 *   payload or `asOf`). Re-issue fetches when what they depend on changed (AE9).
 * - `exited` is teardown: the root drops the slice, so there is no hook; a
 *   result Message arriving afterwards is a no-op.
 * - `update` handles the page's own Messages. Facts for the root go out as
 *   `outMessage` (add a `foldOutMessage` for the page in `src/app/update.ts`).
 * - `view` receives `Shared` as its view inputs.
 */
export type Page<Model, Message, OutMessage = never> = Readonly<{
	init: (
		route: Route,
		shared: Shared,
	) => PageReturn<Model, Message, OutMessage>;
	changedRoute: (
		model: Model,
		previous: Route,
		next: Route,
		shared: Shared,
	) => PageReturn<Model, Message>;
	update: (
		model: Model,
		message: Message,
		shared: Shared,
	) => PageReturn<Model, Message, OutMessage>;
}>;

/** Appends a cursor-paginated page onto `current`, or replaces it when `cursor` is 0 (a fresh fetch). */
export const appendPage = <T>(
	current: { items: ReadonlyArray<T>; nextCursor: Option.Option<number> },
	cursor: number,
	page: { data: ReadonlyArray<T>; nextCursor?: number | undefined },
) => ({
	items: cursor === 0 ? page.data : [...current.items, ...page.data],
	nextCursor: Option.fromNullishOr(page.nextCursor),
});
